/**
 * The contractor portal's own top-level app — a full split from the staff
 * `App()` and from `PortalApp.tsx`, not a branch inside either.
 *
 * Same reasoning as `PortalApp.tsx`: a contractor-portal identity has no
 * `/api/me` row, no company, no membership, and its session lives in a third
 * Better Auth cookie. Splitting at the component boundary, before any
 * staff-only or client-portal-only hook runs, is what keeps all three
 * identities from ever touching.
 */
import { ZERO, add } from "@dantria/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { ContractorPortalSignIn } from "./ContractorPortalSignIn";
import { ApiError, api, fmtAmount as fmt, formatPercent, unwrap } from "./api";
import { contractorPortalSignOut } from "./contractor-portal-auth-client";
import { Button, ChevronButton, Empty, ErrorNote, Loading, Panel, Stat, Table, Td, Th } from "./ui";

async function fetchContractorPortalMe() {
  return unwrap(await api.api["contractor-portal"].me.$get());
}

async function fetchContractorPortalOverview() {
  return unwrap(await api.api["contractor-portal"].overview.$get());
}

async function fetchCommitmentDraws(commitmentId: string) {
  return unwrap(
    await api.api["contractor-portal"].commitments[":id"].draws.$get({
      param: { id: commitmentId },
    }),
  );
}

async function fetchContractorBills() {
  return unwrap(await api.api["contractor-portal"].bills.$get());
}

export function ContractorPortalApp() {
  const [signedOut, setSignedOut] = useState(false);
  const qc = useQueryClient();

  // A 401 here just means "not signed in yet" — not a failure worth a toast.
  const me = useQuery({
    queryKey: ["contractor-portal-me"],
    queryFn: fetchContractorPortalMe,
    retry: false,
    meta: { silent: true },
  });

  // The invite-acceptance path replaces the URL with /contractor-portal once
  // a session exists, but a stale /contractor-portal/invite/:token in the
  // address bar (back button, a re-opened tab) shouldn't keep showing the
  // invite screen once signed in.
  useEffect(() => {
    if (me.data && window.location.pathname !== "/contractor-portal") {
      window.history.replaceState({}, "", "/contractor-portal");
    }
  }, [me.data]);

  if (me.isPending) return <Centered>Loading…</Centered>;

  if (me.isError || signedOut) {
    return (
      <ContractorPortalSignIn
        onSignedIn={() => {
          setSignedOut(false);
          qc.invalidateQueries();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="sticky top-0 z-30 h-14 border-b border-neutral-200 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-[1200px] items-center gap-4 px-6">
          <span className="whitespace-nowrap font-[family-name:--font-display] text-lg font-bold tracking-tight text-neutral-900">
            {me.data.company.name}
          </span>
          <span className="text-sm text-neutral-400">{me.data.contractor.name}</span>
          <span className="ml-auto truncate text-sm text-neutral-500">
            {me.data.contractorPortalUser.email}
          </span>
          <button
            type="button"
            onClick={async () => {
              await contractorPortalSignOut();
              qc.clear();
              setSignedOut(true);
            }}
            className="text-sm text-neutral-600 underline underline-offset-4 hover:text-neutral-900"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-8">
        <Overview contractorName={me.data.contractor.name} />
        <div className="mt-6">
          <RequestHistory />
        </div>
      </main>
    </div>
  );
}

function Overview({ contractorName }: { contractorName: string }) {
  const overview = useQuery({
    queryKey: ["contractor-portal-overview"],
    queryFn: fetchContractorPortalOverview,
  });
  // Which projects have their draw schedule open — a set, so more than one
  // can be read side by side.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (overview.isPending) return <Loading />;
  if (overview.isError) {
    return <ErrorNote>{(overview.error as Error).message}</ErrorNote>;
  }

  const projects = overview.data.projects;
  const currency = projects[0]?.currency ?? "USD";
  const totals = projects.reduce(
    (acc, p) => ({
      contract: add(acc.contract, p.contractValue),
      billed: add(acc.billed, p.billed),
      paid: add(acc.paid, p.paid),
      stillToCome: add(acc.stillToCome, p.stillToCome),
    }),
    { contract: ZERO, billed: ZERO, paid: ZERO, stillToCome: ZERO },
  );

  return (
    <Panel title={`What ${contractorName} is owed`}>
      {projects.length === 0 ? (
        <Empty title="No projects yet">Nothing has been set up for you to see yet.</Empty>
      ) : (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-4">
            <Stat label="Total" value={fmt(totals.contract)} currency={currency} />
            <Stat label="Billed" value={fmt(totals.billed)} currency={currency} />
            <Stat label="Paid" value={fmt(totals.paid)} currency={currency} />
            <Stat
              label="Still to come"
              value={fmt(totals.stillToCome)}
              currency={currency}
              hint="Committed, not yet billed"
            />
          </div>

          <Table
            head={
              <>
                <Th>Code</Th>
                <Th>Project</Th>
                <Th>Status</Th>
                <Th right>Total</Th>
                <Th right>Billed</Th>
                <Th right>Paid</Th>
                <Th right>Still to come</Th>
                <Th />
              </>
            }
          >
            {projects.map((p) => {
              const isOpen = expanded.has(p.id);
              return (
                <Fragment key={p.id}>
                  <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                    <Td mono muted>
                      {p.code}
                    </Td>
                    <Td>{p.name}</Td>
                    <Td muted>{p.status.replace(/_/g, " ")}</Td>
                    <Td right mono>
                      {fmt(p.contractValue, p.currency)}
                    </Td>
                    <Td right mono muted>
                      {fmt(p.billed, p.currency)}
                    </Td>
                    <Td right mono>
                      {fmt(p.paid, p.currency)}
                    </Td>
                    <Td right mono>
                      {fmt(p.stillToCome, p.currency)}
                    </Td>
                    <Td right>
                      <ChevronButton open={isOpen} onClick={() => toggle(p.id)} label="draws" />
                    </Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={8} className="bg-neutral-50 px-4 py-4">
                        <div className="space-y-4">
                          {p.commitmentIds.map((commitmentId) => (
                            <DrawSchedule key={commitmentId} commitmentId={commitmentId} />
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>
        </>
      )}
    </Panel>
  );
}

/**
 * One commitment's draw schedule, with the one write this portal can make: a
 * "Submit for billing" on the next open draw. It never posts to the ledger
 * itself — the request sits pending until a staff admin approves it, which
 * is enforced server-side in `apps/api/src/routes/contractor-portal.ts`, not
 * just left off this form.
 */
function DrawSchedule({ commitmentId }: { commitmentId: string }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busyDrawId, setBusyDrawId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["contractor-portal-draws", commitmentId],
    queryFn: () => fetchCommitmentDraws(commitmentId),
  });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const milestones = q.data.schedule.milestones;
  const nextOpen = milestones.find((m) => !m.frozen);

  async function submit(drawId: string) {
    setError(null);
    setBusyDrawId(drawId);
    try {
      await unwrap(
        await api.api["contractor-portal"].commitments[":id"]["bill-requests"].$post({
          param: { id: commitmentId },
          json: { drawId },
        }),
      );
      qc.invalidateQueries({ queryKey: ["contractor-portal-draws", commitmentId] });
      qc.invalidateQueries({ queryKey: ["contractor-portal-overview"] });
      qc.invalidateQueries({ queryKey: ["contractor-portal-bills"] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusyDrawId(null);
    }
  }

  const { commitment, resolved } = q.data;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <h4 className="mb-2 text-sm font-medium text-neutral-700">
        {commitment.scope ||
          (commitment.basis === "fixed" ? "Fixed commitment" : "Share of contract")}
        <span className="ml-2 font-normal text-neutral-400">
          {commitment.basis === "percent_of_contract" &&
            `${formatPercent(commitment.percent ?? "0")} of contract · `}
          {fmt(resolved.value, commitment.currency)}
        </span>
      </h4>
      {error && <ErrorNote>{error}</ErrorNote>}
      <Table
        maxHeight={false}
        head={
          <>
            <Th>Draw</Th>
            <Th>Status</Th>
            <Th right>Amount</Th>
            <Th />
          </>
        }
      >
        {milestones.map((m) => {
          const settlement = q.data.settlements[m.id];
          return (
            <tr key={m.id} className="border-b border-neutral-100 last:border-0">
              <Td>{m.name}</Td>
              <Td muted>{m.frozen ? (settlement?.status ?? "billed") : m.status}</Td>
              <Td right mono>
                {fmt(m.amount)}
              </Td>
              <Td right>
                {!m.frozen && m.id === nextOpen?.id && (
                  <Button
                    variant="ghost"
                    disabled={busyDrawId === m.id}
                    onClick={() => submit(m.id)}
                  >
                    {busyDrawId === m.id ? "Submitting…" : "Submit for billing"}
                  </Button>
                )}
              </Td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}

/** Every bill this contractor has submitted, and where it stands. */
function RequestHistory() {
  const q = useQuery({ queryKey: ["contractor-portal-bills"], queryFn: fetchContractorBills });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;
  if (q.data.bills.length === 0) return null;

  return (
    <Panel title="Your requests">
      <Table
        head={
          <>
            <Th>Project</Th>
            <Th>Commitment</Th>
            <Th>Draw</Th>
            <Th>Issued</Th>
            <Th right>Total</Th>
            <Th>Status</Th>
          </>
        }
      >
        {q.data.bills.map((b) => (
          <tr key={b.id} className="border-b border-neutral-100 last:border-0 even:bg-neutral-50">
            <Td muted>{b.projectName ?? "—"}</Td>
            <Td muted>{commitmentLabel(b)}</Td>
            <Td muted>{b.drawName ?? "—"}</Td>
            <Td mono muted>
              {b.issueDate}
            </Td>
            <Td right mono>
              {fmt(b.total, b.currency)}
            </Td>
            <Td muted>{statusLabel(b.status)}</Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

/** The same label the draw schedule uses for a commitment — scope if it has one, else its shape. */
function commitmentLabel(b: {
  commitmentScope: string | null;
  commitmentBasis: string | null;
}): string {
  if (b.commitmentScope) return b.commitmentScope;
  if (b.commitmentBasis === "percent_of_contract") return "Share of contract";
  if (b.commitmentBasis === "fixed") return "Fixed commitment";
  return "—";
}

function statusLabel(status: string): string {
  switch (status) {
    case "draft":
      return "Pending approval";
    case "void":
      return "Rejected";
    default:
      return status.replace(/_/g, " ");
  }
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50">
      <div className="text-center">{children}</div>
    </div>
  );
}
